import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSupabaseUrl, getSupabaseAnonKey } from '../lib/environments';
import { getAccessToken, supabase } from '../lib/supabase';
import { linksApi } from '../services/linksApi';

interface InviteData {
  inviter_name: string;
  inviter_avatar: string | null;
  email: string | null;
  phone: string | null;
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'expired' }
  | { kind: 'wrong-account'; data: InviteData }    // session user doesn't match invitation
  | { kind: 'not-logged-in'; data: InviteData }    // show Join Kizu CTA
  | { kind: 'accepting'; data: InviteData }        // auto-accept in flight
  | { kind: 'error'; data: InviteData; message: string };

export const TokenInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [view, setView] = useState<ViewState>({ kind: 'loading' });

  useEffect(() => {
    if (!token) { setView({ kind: 'expired' }); return; }

    let cancelled = false;
    (async () => {
      // 1. Fetch invitation metadata (public endpoint)
      let data: InviteData;
      try {
        const res = await fetch(
          `${getSupabaseUrl()}/functions/v1/share-invite-page?token=${token}&format=json`,
          { headers: { apikey: getSupabaseAnonKey() } },
        );
        if (!res.ok) { if (!cancelled) setView({ kind: 'expired' }); return; }
        const json = await res.json();
        if (json.error) { if (!cancelled) setView({ kind: 'expired' }); return; }
        data = json as InviteData;
      } catch {
        if (!cancelled) setView({ kind: 'expired' });
        return;
      }
      if (cancelled) return;

      // 2. If no session, show the invite preview with a Join CTA.
      const accessToken = getAccessToken();
      if (!accessToken) { setView({ kind: 'not-logged-in', data }); return; }

      // 3. Session exists — try to auto-accept. Server enforces ownership
      // (email/phone match); we just react to the response code.
      setView({ kind: 'accepting', data });
      const result = await linksApi.acceptInvitation(token);
      if (cancelled) return;

      if (result.success && result.data?.conversation_id) {
        navigate(`/mobile/chat/${result.data.conversation_id}`, { replace: true });
        return;
      }
      const errorMsg = result.error || 'Failed to accept invitation';
      if (errorMsg.toLowerCase().includes('not for you')) {
        setView({ kind: 'wrong-account', data });
      } else {
        setView({ kind: 'error', data, message: errorMsg });
      }
    })();

    return () => { cancelled = true; };
  }, [token, navigate]);

  const switchAccount = async () => {
    await supabase.auth.signOut();
    navigate(`/signup?invite_token=${token}`, { replace: true });
  };

  const goToSignup = () => navigate(`/signup?invite_token=${token}`);

  if (view.kind === 'loading' || view.kind === 'accepting') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner} />
          {view.kind === 'accepting' && (
            <p style={styles.description}>Connecting your Kizu…</p>
          )}
        </div>
      </div>
    );
  }

  if (view.kind === 'expired') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>Invitation Expired</h1>
          <p style={styles.description}>This invitation is no longer valid.</p>
          <a href="https://kizu.online" style={styles.button}>Visit Kizu</a>
        </div>
      </div>
    );
  }

  const { data } = view;
  const name = data.inviter_name || 'Someone';
  const initial = name.charAt(0).toUpperCase();

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {data.inviter_avatar ? (
          <img src={data.inviter_avatar} alt={name} style={styles.avatar} />
        ) : (
          <div style={styles.avatarPlaceholder}><span>{initial}</span></div>
        )}
        <h1 style={styles.title}>{name} invited you to Kizu</h1>
        {view.kind === 'wrong-account' ? (
          <>
            <p style={styles.description}>
              This invitation was sent to a different account than the one you're signed in with.
            </p>
            <button onClick={switchAccount} style={styles.button}>
              Switch Account
            </button>
          </>
        ) : view.kind === 'error' ? (
          <>
            <p style={styles.description}>{view.message}</p>
            <button onClick={goToSignup} style={styles.button}>
              Try Again
            </button>
          </>
        ) : (
          <>
            <p style={styles.description}>Where your digital memories live.</p>
            <button onClick={goToSignup} style={styles.button}>
              Join Kizu
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh', background: '#0a0a0a', color: '#fff',
    display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 24,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  card: { textAlign: 'center', maxWidth: 400 },
  avatar: {
    width: 96, height: 96, borderRadius: '50%', objectFit: 'cover',
    marginBottom: 20, border: '3px solid #333',
  },
  avatarPlaceholder: {
    width: 96, height: 96, borderRadius: '50%', background: '#222',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 36, marginBottom: 20, border: '3px solid #333', color: '#fff',
  },
  title: { fontSize: 22, fontWeight: 600, marginBottom: 8 },
  description: { fontSize: 16, color: '#999', marginBottom: 32, lineHeight: 1.5 },
  button: {
    display: 'inline-block', background: '#4A90D9', color: '#fff',
    padding: '16px 40px', borderRadius: 12, border: 'none', cursor: 'pointer',
    fontSize: 18, fontWeight: 600, width: '100%',
  },
  spinner: {
    width: 32, height: 32, border: '3px solid #333', borderTop: '3px solid #4A90D9',
    borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto',
  },
};

export default TokenInvite;
